/**
 * FleetStatusPage
 * Main dashboard page showing fleet overview and machine list
 */

import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Zap, AlertTriangle, WifiOff, Users, UserCheck, UserX, Clock, CreditCard, Home, Banknote, XCircle, Battery, PauseCircle } from 'lucide-react';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/core/components/ui/hover-card';
import { useOperatorStore } from '@/core/stores/operator-store';
import { useFleetStore } from '../../stores/fleet-store';
import { useSessionsStore } from '../../stores/sessions-store';
import { useAccountsStore } from '../../stores/accounts-store';
import { useAnalyticsStore } from '../../stores/analytics-store';
import { StatCard } from '../atoms/StatCard';
import { ConnectorDonutChart } from '../molecules/ConnectorDonutChart';
import { MachineDonutChart } from '../molecules/MachineDonutChart';
import { PollingIndicator } from '../molecules/PollingIndicator';
import { ActiveSessionsPanel } from '../organisms/ActiveSessionsPanel';
import { ChargerMapPanel } from '../molecules/ChargerMapPanel';
import { PageSkeleton } from '../atoms/SkeletonCard';
import { MachineStatus } from '../../types/dashboard-types';
import { formatNumber } from '../../utils/currency-utils';
import { useCurrencyFormatterRight } from '../../hooks/useCurrencyFormatter';

export function FleetStatusPage() {
  const { selectedOperator } = useOperatorStore();
  const formatCurrency = useCurrencyFormatterRight();

  const {
    summary,
    connectorBreakdown,
    machines,
    totalMachines,
    totalPages,
    currentPage,
    pageSize,
    filters,
    mapPoints,
    isLoadingMap,
    mapError,
    isLoadingSummary,
    isLoadingMachines,
    isRefreshing,
    summaryError,
    machinesError,
    lastSummaryUpdate,
    loadSummary,
    loadMachines,
    loadMapPoints,
    loadPage,
    setPageSize,
    setFilters,
    clearFilters,
    refreshAll,
    startPolling,
    stopPolling,
  } = useFleetStore();

  const {
    activeSessions,
    totalActiveCount,
    isLoadingActive,
    activeError,
    loadActiveSessions,
    startPolling: startSessionsPolling,
    stopPolling: stopSessionsPolling,
  } = useSessionsStore();

  const {
    summary: accountsSummary,
    isLoadingSummary: isLoadingAccounts,
    loadSummary: loadAccountsSummary,
  } = useAccountsStore();

  const {
    summary: analyticsSummary,
    isLoading: isLoadingAnalytics,
    setPreset: setAnalyticsPreset,
  } = useAnalyticsStore();

  // Initialize on mount
  useEffect(() => {
    if (selectedOperator?.operatorId) {
      loadSummary();
      loadMachines();
      loadMapPoints();
      loadActiveSessions();
      loadAccountsSummary();

      const { selectedPreset: prevPreset, dateRange: prevDateRange } = useAnalyticsStore.getState();
      setAnalyticsPreset('today'); // Load today's revenue data
      startPolling();
      startSessionsPolling();

      return () => {
        stopPolling();
        stopSessionsPolling();
        useAnalyticsStore.setState({ selectedPreset: prevPreset, dateRange: prevDateRange });
      };
    }

    return () => {
      stopPolling();
      stopSessionsPolling();
    };
  }, [selectedOperator?.operatorId, loadAccountsSummary, setAnalyticsPreset]);

  // Handle summary card clicks to filter by OCPP machine status
  const handleCardClick = (filter: string) => {
    if (filter === 'all') {
      clearFilters();
    } else if (filter === 'available') {
      setFilters({ status: ['Available'] as MachineStatus[] });
    } else if (filter === 'in-session') {
      // Group all active session statuses (excluding Preparing - it's authorization phase)
      setFilters({ status: ['Charging', 'Finishing', 'SuspendedEV', 'SuspendedEVSE'] as MachineStatus[] });
    } else if (filter === 'preparing') {
      setFilters({ status: ['Preparing'] as MachineStatus[], sessionStatus: 'Preparing' as MachineStatus });
    } else if (filter === 'charging') {
      setFilters({ status: ['Charging'] as MachineStatus[], sessionStatus: 'Charging' as MachineStatus });
    } else if (filter === 'finishing') {
      setFilters({ status: ['Finishing'] as MachineStatus[], sessionStatus: 'Finishing' as MachineStatus });
    } else if (filter === 'suspendedEV') {
      setFilters({ status: ['SuspendedEV'] as MachineStatus[], sessionStatus: 'SuspendedEV' as MachineStatus });
    } else if (filter === 'suspendedEVSE') {
      setFilters({ status: ['SuspendedEVSE'] as MachineStatus[], sessionStatus: 'SuspendedEVSE' as MachineStatus });
    } else if (filter === 'faulted') {
      setFilters({ status: ['Faulted'] as MachineStatus[] });
    } else if (filter === 'offline') {
      // Offline machines (connectivity issues) - show as Unavailable for now
      setFilters({ status: ['Unavailable'] as MachineStatus[] });
    } else if (filter === 'unavailable') {
      setFilters({ status: ['Unavailable'] as MachineStatus[] });
    } else if (filter === 'reserved') {
      setFilters({ status: ['Reserved'] as MachineStatus[] });
    }
  };

  // Determine active filter for highlighting cards
  const activeFilter = filters.status?.length === 1
    ? filters.status[0].toLowerCase()
    : filters.status?.length === 0 || !filters.status
      ? 'all'
      : undefined;

  // Show skeleton on initial load
  if (isLoadingSummary && !summary) {
    return <PageSkeleton />;
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-end px-6 py-2 bg-white">
        <PollingIndicator
          lastUpdated={lastSummaryUpdate}
          isRefreshing={isRefreshing}
          onRefresh={refreshAll}
          showLabel={false}
        />
      </div>

      {/* Content - 4 sections */}
      <div className="px-6 pb-8 space-y-4 bg-white">
        {/* Section 1: Charging Stations */}
        <div className="bg-white rounded-lg p-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Charging Stations</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 lg:grid-rows-3">
            {/* Available Connectors */}
            <div className="lg:col-start-1 lg:row-start-1">
              <StatCard
                title="Available Connectors"
                value={formatNumber(summary?.availableConnectors || 0, 0)}
                icon={CheckCircle}
                iconColor="text-green-600"
                iconBgColor="bg-green-100"
                isLoading={isLoadingSummary}
              />
            </div>

            {/* Occupied Connectors with Session Breakdown HoverCard */}
            <div className="lg:col-start-1 lg:row-start-2">
              <HoverCard openDelay={200} closeDelay={300}>
                <HoverCardTrigger asChild>
                  <div>
                    <StatCard
                      title="Occupied Connectors"
                      value={formatNumber(
                        (summary?.chargingConnectors || 0) +
                        (summary?.finishingConnectors || 0) +
                        (summary?.suspendedEVConnectors || 0) +
                        (summary?.suspendedEVSEConnectors || 0),
                        0
                      )}
                      icon={Zap}
                      iconColor="text-blue-600"
                      iconBgColor="bg-blue-100"
                      showPulse
                      isLoading={isLoadingSummary}
                    />
                  </div>
                </HoverCardTrigger>
                <HoverCardContent className="w-[480px] p-4" align="start" side="bottom">
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Session Status Breakdown</h4>
                    <div className="grid grid-cols-4 gap-2">
                      <StatCard
                        title="Charging"
                        value={formatNumber(summary?.chargingConnectors || 0, 0)}
                        subtitle="Active"
                        icon={Battery}
                        iconColor="text-blue-500"
                        size="sm"
                        showPulse
                        onClick={() => handleCardClick('charging')}
                        isActive={activeFilter === 'charging'}
                        isLoading={isLoadingSummary}
                      />

                      <StatCard
                        title="Suspended (EV)"
                        value={formatNumber(summary?.suspendedEVConnectors || 0, 0)}
                        subtitle="Vehicle"
                        icon={PauseCircle}
                        iconColor="text-orange-500"
                        size="sm"
                        onClick={() => handleCardClick('suspendedEV')}
                        isActive={activeFilter === 'suspendedEV'}
                        isLoading={isLoadingSummary}
                      />

                      <StatCard
                        title="Suspended (EVSE)"
                        value={formatNumber(summary?.suspendedEVSEConnectors || 0, 0)}
                        subtitle="Station"
                        icon={PauseCircle}
                        iconColor="text-orange-500"
                        size="sm"
                        onClick={() => handleCardClick('suspendedEVSE')}
                        isActive={activeFilter === 'suspendedEVSE'}
                        isLoading={isLoadingSummary}
                      />

                      <StatCard
                        title="Finishing"
                        value={formatNumber(summary?.finishingConnectors || 0, 0)}
                        subtitle="Ending"
                        icon={CheckCircle}
                        iconColor="text-teal-500"
                        size="sm"
                        onClick={() => handleCardClick('finishing')}
                        isActive={activeFilter === 'finishing'}
                        isLoading={isLoadingSummary}
                      />
                    </div>
                  </div>
                </HoverCardContent>
              </HoverCard>
            </div>

            {/* Faulted Connectors */}
            <div className="lg:col-start-1 lg:row-start-3">
              <StatCard
                title="Faulted Connectors"
                value={formatNumber(summary?.faultedConnectors || 0, 0)}
                icon={AlertTriangle}
                iconColor="text-red-600"
                iconBgColor="bg-red-100"
                isLoading={isLoadingSummary}
              />
            </div>

            {/* Offline < 1 Day */}
            <div className="lg:col-start-2 lg:row-start-1">
              <StatCard
                title="Offline < 1 Day"
                value={formatNumber(summary?.offlineConnectorsLessThan1Day || 0, 0)}
                icon={WifiOff}
                iconColor="text-yellow-600"
                iconBgColor="bg-yellow-100"
                isLoading={isLoadingSummary}
              />
            </div>

            {/* Offline < 1 Week */}
            <div className="lg:col-start-2 lg:row-start-2">
              <StatCard
                title="Offline < 1 Week"
                value={formatNumber(summary?.offlineConnectorsLessThan1Week || 0, 0)}
                icon={WifiOff}
                iconColor="text-orange-600"
                iconBgColor="bg-orange-100"
                isLoading={isLoadingSummary}
              />
            </div>

            {/* Offline > 1 Week */}
            <div className="lg:col-start-2 lg:row-start-3">
              <StatCard
                title="Offline > 1 Week"
                value={formatNumber(summary?.offlineConnectorsMoreThan1Week || 0, 0)}
                icon={WifiOff}
                iconColor="text-gray-600"
                iconBgColor="bg-gray-100"
                isLoading={isLoadingSummary}
              />
            </div>

            {/* Connector Chart */}
            <div className="lg:col-start-3 lg:row-start-1 lg:row-span-3">
              <ConnectorDonutChart
                data={connectorBreakdown}
                totalConnectors={summary?.totalConnectors || 0}
                isLoading={isLoadingSummary}
              />
            </div>

            {/* Station Chart */}
            <div className="lg:col-start-4 lg:row-start-1 lg:row-span-3">
              <MachineDonutChart
                onlineMachines={summary?.onlineMachines || 0}
                disconnectedMachines={summary?.disconnectedMachines || 0}
                totalMachines={summary?.totalMachines || 0}
                isLoading={isLoadingSummary}
                title="Charging Stations Status"
              />
            </div>
          </div>

          <div className="mt-4">
            <ChargerMapPanel points={mapPoints} isLoading={isLoadingMap} error={mapError} currency={selectedOperator?.currency} />
          </div>
        </div>

        {/* Section 3: Accounts */}
        <div className="bg-white rounded-lg p-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Accounts</h2>

          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
          <StatCard
            title="Total Accounts"
            value={formatNumber(accountsSummary?.totalAccounts || 0, 0)}
            icon={Users}
            iconColor="text-purple-600"
            iconBgColor="bg-purple-100"
            isLoading={isLoadingAccounts}
          />
          <StatCard
            title="Active Accounts"
            value={formatNumber(accountsSummary?.activeAccounts || 0, 0)}
            icon={UserCheck}
            iconColor="text-green-600"
            iconBgColor="bg-green-100"
            isLoading={isLoadingAccounts}
          />
          <StatCard
            title="Blocked Accounts"
            value={formatNumber(accountsSummary?.blockedAccounts || 0, 0)}
            icon={UserX}
            iconColor="text-red-600"
            iconBgColor="bg-red-100"
            isLoading={isLoadingAccounts}
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <StatCard
            title="Pending Accounts"
            value={formatNumber(accountsSummary?.pendingAccounts || 0, 0)}
            icon={Clock}
            iconColor="text-orange-600"
            iconBgColor="bg-orange-100"
            isLoading={isLoadingAccounts}
          />
          <StatCard
            title="Active Cards Expiring Soon"
            value={formatNumber((accountsSummary?.expiredPaymentCards || 0) + (accountsSummary?.expiringPaymentCards || 0), 0)}
            icon={CreditCard}
            iconColor="text-yellow-600"
            iconBgColor="bg-yellow-100"
            isLoading={isLoadingAccounts}
          />
          <StatCard
            title="Private Charging Stations"
            value={formatNumber(accountsSummary?.activeSubscriptions || 0, 0)}
            icon={Home}
            iconColor="text-blue-600"
            iconBgColor="bg-blue-100"
            isLoading={isLoadingAccounts}
          />
        </div>
          </div>
        </div>

        {/* Section 4: Payments Today */}
        <div className="bg-white rounded-lg p-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Payments Today</h2>

          <div className="grid grid-cols-2 gap-3">
            <StatCard
              title="Successful Payments"
              value={formatCurrency(analyticsSummary?.totalRevenue || 0)}
              subtitle={undefined}
              icon={Banknote}
              iconColor="text-green-600"
              iconBgColor="bg-green-100"
              isLoading={isLoadingAnalytics || isLoadingAccounts}
            />
            <StatCard
              title="Failed Payments"
              value={formatNumber(accountsSummary?.failedPaymentsToday || 0, 0)}
              subtitle="Unique by Transaction ID"
              icon={XCircle}
              iconColor="text-red-600"
              iconBgColor="bg-red-100"
              isLoading={isLoadingAccounts}
            />
          </div>
        </div>

        {/* Section 5: Live Sessions */}
        <div className="bg-white rounded-lg p-4">
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Live Sessions ({formatNumber(totalActiveCount, 0)})
            </h2>
            <Link to="/sessions-report" className="text-sm text-blue-600 hover:text-blue-800 font-medium underline">
              All
            </Link>
          </div>
          <ActiveSessionsPanel
            sessions={activeSessions}
            isLoading={isLoadingActive}
            error={activeError}
          />
        </div>

        {/* Filters */}
        {/* <div className="bg-white p-4 rounded-lg border">
          <MachineSearchFilter
            filters={filters}
            onFiltersChange={setFilters}
            onClearFilters={clearFilters}
          />
        </div> */}

        {/* Machine List */}
        {/* <MachineListTable
          machines={machines}
          totalCount={totalMachines}
          totalPages={totalPages}
          currentPage={currentPage}
          pageSize={pageSize}
          isLoading={isLoadingMachines}
          error={machinesError}
          onPageChange={loadPage}
          onPageSizeChange={setPageSize}
        /> */}
      </div>
    </div>
  );
}
